import React, { useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import {
  Text,
  Loader,
  Button,
  Input,
  FieldGroup,
  Header,
  Modal,
  Link,
  Base,
} from '@pet-notify/ui'

import intellitagPackagingImage from './images/intellitag-packaging.jpg'
import petLightPackagingImage from './images/pet-light-packaging.jpg'
import vaultImage from './images/vault.jpg'
import ButtonGroup from 'components/ButtonGroup'
import { useRedemptionCodes } from 'hooks/redemption-codes'
import ErrorMessage from 'components/ErrorMessage'
import type { OrderRedemptionInput } from 'services/order-redemtions'
import style from './style.module.css'

type FormData = {
  redemptionCodes: { code: string }[]
}

type Props = {
  onSubmit: (v: string[]) => void
  state: OrderRedemptionInput
}

function RedemptionCodesPage({ state, ...props }: Props) {
  const [showGifting, setShowGifting] = useState(false)
  const [codes, setCodes] = useState<string[]>([])
  const { register, control, handleSubmit } = useForm<FormData>({
    defaultValues: {
      redemptionCodes: state.codes?.map((code) => ({ code })) || [{ code: '' }],
    },
  })

  const redemptionCodes = useFieldArray({ control, name: 'redemptionCodes' })

  const redemptionCodesQueries = useRedemptionCodes(codes)

  const onSubmit = handleSubmit(({ redemptionCodes }) => {
    setCodes(redemptionCodes.map(({ code }) => code.trim()))
  })

  const handleAdd = () => redemptionCodes.append({ code: '' })

  const isLoading = useMemo(() => {
    return redemptionCodesQueries.some(({ isLoading }) => isLoading)
  }, [redemptionCodesQueries])

  const isSuccess = useMemo(() => {
    return (
      redemptionCodesQueries.length > 0 &&
      redemptionCodesQueries.every(({ isSuccess }) => isSuccess)
    )
  }, [redemptionCodesQueries])

  const numberOfIntellitags = useMemo(() => {
    return redemptionCodesQueries.reduce((acc, query) => {
      if (query.isSuccess) {
        return acc + 1
      }
      return acc
    }, 0)
  }, [redemptionCodesQueries])

  const numberOfPetLights = useMemo(() => {
    return redemptionCodesQueries.reduce((acc, query) => {
      if (query.isSuccess && query.data?.redemptionPackage.key === 'igive-2') {
        return acc + 1
      }
      return acc
    }, 0)
  }, [redemptionCodesQueries])

  return (
    <>
      <Header as='h2' mb='4' size='xlarge'>
        Intellitag<sup>&reg;</sup>
      </Header>

      <Header as='h3' mb='4' size='medium'>
        The Smart Pet Tag for Smart Pet Parents<sup>&trade;</sup>
      </Header>

      <div className={style.image}>
        <img src={vaultImage} alt='Pet Vault on iPhone and Intellitag' />
      </div>

      <Header as='h2' mb='4' size='medium'>
        Welcome to Pet Notify Intellitag!
      </Header>

      <Text mb='8'>
        Intellitag instantly connects you with your pet&apos;s rescuer and
        provides the rescuer with complete pet-care instructions stored in your
        pet&apos;s vault. Simultaneously, Intellitag calls, emails, and texts
        you and your backup network that your lost pet&apos;s found for total
        pet protection.
      </Text>

      <form onSubmit={onSubmit}>
        <Header as='h3' size='small' mb='4'>
          Enter your redemption code{redemptionCodes.fields.length > 1 && 's'}
        </Header>

        {redemptionCodes.fields.map((field, index) => (
          <Base key={field.id} mb='4'>
            <FieldGroup key={field.id} mb='2'>
              <Input
                type='text'
                required
                {...register(`redemptionCodes.${index}.code`)}
              />
            </FieldGroup>
            {redemptionCodes.fields.length > 1 && (
              <div className={style.removeLinkWrapper}>
                <Link
                  variant='cancel'
                  onClick={() => redemptionCodes.remove(index)}
                >
                  Remove
                </Link>
              </div>
            )}
            {redemptionCodesQueries[index]?.isError && (
              <ErrorMessage error={redemptionCodesQueries[index].error} />
            )}
          </Base>
        ))}

        {isLoading ? (
          <Loader />
        ) : (
          <ButtonGroup mb='8'>
            <Button type='submit'>Continue</Button>
            <Button type='button' variant='hollow' onClick={handleAdd}>
              Add another redemption code
            </Button>
          </ButtonGroup>
        )}
      </form>

      <Text mb='4'>
        <Link onClick={() => setShowGifting(true)}>
          Gifting your Intellitag?
        </Link>
      </Text>
      <Text mb='6'>
        <Link href='https://www.petnotify.com/' target='_blank'>
          Learn more about Pet Notify
        </Link>
      </Text>
      <Text mb='6'>
        Have a question? Contact us at{' '}
        <Link href='mailto:support@petnotify.com'>support@petnotify.com</Link>.
      </Text>
      {isSuccess && (
        <Modal onClose={() => setCodes([])}>
          <Header as='h2' mb='4' size='xlarge'>
            Thank you for your purchase!
          </Header>

          <Header as='h3' mb='4' size='small'>
            Your purchase includes the following...
          </Header>

          <Base as='ul' className={style.list} mb='4'>
            <li>
              <Text>
                <strong>{numberOfIntellitags}</strong> Intellitag
                {numberOfIntellitags > 1 && 's'}
              </Text>
              <img
                width='100'
                height='100'
                src={intellitagPackagingImage}
                alt='Intellitag Packaging'
              />
            </li>
            {numberOfPetLights > 0 && (
              <li>
                <Text>
                  <strong>{numberOfPetLights}</strong> Pet Light
                  {numberOfPetLights > 1 && 's'}
                </Text>
                <img
                  width='100'
                  height='100'
                  src={petLightPackagingImage}
                  alt='Pet Light Packaging'
                />
              </li>
            )}
            <li>
              <Text>
                <strong>12</strong> months free Intellitag pet monitoring
              </Text>
            </li>
          </Base>

          <Text mb='8'>
            Now let&apos;s create your Pet Notify account and customize your
            Intellitag.
          </Text>

          <Button onClick={() => props.onSubmit(codes)}>Create account</Button>
        </Modal>
      )}
      {showGifting && (
        <Modal onClose={() => setShowGifting(false)}>
          <Header as='h2' mb='4'>
            Gifting your Intellitag?
          </Header>
          <Text>
            Gifted Intellitags require the recipient to create their personal
            Pet Notify account at{' '}
            <Link href='https://app.petnotify.com/give/'>
              https://app.petnotify.com/give/
            </Link>{' '}
            along with the gifted confirmation code.
          </Text>
        </Modal>
      )}
    </>
  )
}

export default RedemptionCodesPage
